/**
 * @file use-stock-levels.ts
 * @description Загрузка и клиентская доводка вкладки «Остатки»
 * (phase19.24 этап 1, шаг 4; серверный отбор «Без места» — правка по ревью
 * 2026-09-20).
 *
 * Серверная часть отбора — склад, фасовка, «только с остатком»
 * (`onlyPositive`), «только продаваемые» (`onlySellable`), «ниже минимума» и
 * «без места» (`unplaced`); на клиенте доводится только поиск (название,
 * артикул и номер партии — SC59, phase19.31).
 * «Без места» раньше фильтровалось по `lot.bin` уже после загрузки — на складе
 * с тысячей партий плитка «Без места: 40» показывала лишь те строки, что
 * попали в выдачу.
 *
 * Нулевые беспартийные уровни (`lotId = null`, `quantity = 0`) прячем всегда:
 * это легаси-строки от перевода расхода на партии, физического смысла у них
 * нет, а в реестре они выглядят пустой строкой (TD-58). Выключенный фильтр
 * «только с остатком» показывает нули по партиям — их кладовщик как раз ищет.
 *
 * `reload` нужен размещению: после проведения документа список перечитывается
 * с сервера, а не правится руками — адрес партии теперь знает только он.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { matchesStockSearch } from "./stock-level-format";
import type { StockFilters, StockLabelsFilter } from "./use-stock-filters";
import { formatSkuName, stockApi, type StockLevelDto } from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";

/**
 * Потолок выдачи реестра остатков. Сервер может прислать `meta.truncated`, но
 * старый его не отдаёт — упор в ровно такое число строк считаем обрезкой сами
 * (то же правило, что у окна журнала движений).
 */
export const STOCK_WINDOW = 500;

/** Отбор экрана → параметр сервера `labelStatus`. */
const LABEL_STATUS_PARAM: Record<StockLabelsFilter, "NONE" | "STALE" | undefined> = {
  "": undefined,
  none: "NONE",
  stale: "STALE",
};

interface StockLevelsApi {
  /** Строки после клиентской доводки — то, что видно на экране. */
  visible: StockLevelDto[];
  loading: boolean;
  error: string | null;
  /** Сервер показал не весь отбор — экран просит сузить склад или поиск. */
  truncated: boolean;
  /** Перечитать список с сервера. */
  reload: () => void;
}

/** Легаси-строка без партии и без количества — показывать нечего (TD-58). */
function isEmptyLegacyLevel(level: StockLevelDto): boolean {
  return level.lotId === null && Number(level.quantity) === 0;
}

export function useStockLevels(filters: StockFilters): StockLevelsApi {
  const [items, setItems] = useState<StockLevelDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [version, setVersion] = useState(0);

  const reload = useCallback(() => setVersion((prev) => prev + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    stockApi
      .list({
        warehouseId: filters.warehouseId || undefined,
        skuId: filters.skuId || undefined,
        onlyPositive: filters.onlyPositive,
        onlySellable: filters.onlySellable,
        belowMin: filters.belowMin,
        unplaced: filters.unplaced,
        // phase19.22 (W17-9c): «Без этикеток» / «Этикетки устарели».
        labelStatus: LABEL_STATUS_PARAM[filters.labels],
      })
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTruncated(result.truncated || result.items.length >= STOCK_WINDOW);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить остатки"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    filters.warehouseId,
    filters.skuId,
    filters.onlyPositive,
    filters.onlySellable,
    filters.belowMin,
    filters.unplaced,
    filters.labels,
    version,
  ]);

  // SC59: один поиск — название, артикул и номер партии. Старый параметр
  // `lot` из ссылок уже подставлен в `search` (см. `useStockFilters`).
  const visible = useMemo(
    () =>
      items.filter(
        (level) => !isEmptyLegacyLevel(level) && matchesStockSearch(level, formatSkuName(level.sku), filters.search),
      ),
    [items, filters.search],
  );

  return { visible, loading, error, truncated, reload };
}
